/**
 * Offer Validation Utilities
 * Pure functions for validation and date calculations
 * Used by useOfferEdit hook and any component needing offer logic
 */

/**
 * Validate business email format
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid email format
 */
export const validateBusinessEmail = (email) => {
  if (!email) return true; // Optional field
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Validate offer form data
 * @param {Object} formData - Form data object
 * @param {string} formData.offer_title - Offer title
 * @param {string} formData.offer_description - Offer description
 * @param {string} formData.business_email - Optional business email
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateOfferForm = (formData) => {
  const errors = [];

  if (!formData.offer_title || formData.offer_title.trim() === '') {
    errors.push('Offer title is required');
  }

  if (!formData.offer_description || formData.offer_description.trim() === '') {
    errors.push('Offer description is required');
  }

  if (!formData.offer_terms || formData.offer_terms.trim() === '') {
    errors.push('Terms & Conditions are required');
  }

  if (formData.business_email && !validateBusinessEmail(formData.business_email)) {
    errors.push('Please enter a valid business email address');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate expiration date from duration
 * @param {string} duration - Duration key (e.g., '1week', '3months')
 * @returns {string} - ISO date string
 */
export const calculateExpirationDate = (duration) => {
  const durationDays = {
    '1week': 7,
    '2weeks': 14,
    '1month': 30,
    '2months': 60,
    '3months': 90,
    '6months': 180,
    '12months': 365
  };

  const days = durationDays[duration] || 30; // Default to 1 month
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
};

/**
 * Calculate time remaining until date
 * @param {string} endDate - ISO date string
 * @returns {string} - Human-readable time remaining
 */
export const calculateTimeRemaining = (endDate) => {
  if (!endDate) return 'N/A';
  
  const now = new Date();
  const end = new Date(endDate);
  const daysRemaining = Math.ceil((end - now) / (1000 * 60 * 60 * 24));

  if (daysRemaining < 0) return 'Expired';
  if (daysRemaining === 0) return 'Expires today';
  if (daysRemaining === 1) return '1 day';
  if (daysRemaining < 30) return `${daysRemaining} days`;

  const months = Math.floor(daysRemaining / 30);
  const days = daysRemaining % 30;
  
  if (days === 0) return `${months} ${months === 1 ? 'month' : 'months'}`;
  return `${months} ${months === 1 ? 'month' : 'months'}, ${days} ${days === 1 ? 'day' : 'days'}`;
};

/**
 * Parse duration from expiration date (reverse calculation)
 * Maps days remaining to closest duration option
 * @param {string} expirationDate - ISO date string
 * @returns {string} - Duration key (e.g., '1month')
 */
export const parseDurationFromDate = (expirationDate) => {
  if (!expirationDate) return '1month';

  const now = new Date();
  const expiration = new Date(expirationDate);
  const daysRemaining = Math.ceil((expiration - now) / (1000 * 60 * 60 * 24));

  // Map to closest duration option
  if (daysRemaining <= 10) return '1week';
  if (daysRemaining <= 17) return '2weeks';
  if (daysRemaining <= 45) return '1month';
  if (daysRemaining <= 75) return '2months';
  if (daysRemaining <= 105) return '3months';
  if (daysRemaining <= 210) return '6months';
  return '12months';
};

/**
 * Get duration options for dropdown
 * @returns {Array} - Array of {value, label} objects
 */
export const getDurationOptions = () => [
  { value: '1week', label: '1 Week' },
  { value: '2weeks', label: '2 Weeks' },
  { value: '1month', label: '1 Month' },
  { value: '2months', label: '2 Months' },
  { value: '3months', label: '3 Months' },
  { value: '6months', label: '6 Months' },
  { value: '12months', label: '12 Months' }
];

/**
 * Get subscription duration options for dropdown
 * @returns {Array} - Array of {value, label} objects
 */
export const getSubscriptionOptions = () => [
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' },
  { value: 9, label: '9 Months' },
  { value: 12, label: '12 Months' }
];
